import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Play, Users, QrCode, Zap, Trophy, Clock, 
  RotateCcw, MapPin, Sparkles, Monitor, HelpCircle, 
  CheckCircle, XCircle, Eye, Image, Video, Volume2,
  ChevronDown, ChevronUp, Home, ScanLine, Crown, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="glass-light rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#E2D9F3]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#E2D9F3]" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="px-4 pb-4"
        >
          {children}
        </motion.div>
      )}
    </div>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-4">
    <div className="w-8 h-8 rounded-full bg-[#8B5CF6] flex items-center justify-center text-white font-bold flex-shrink-0">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="text-white font-semibold mb-1">{title}</h4>
      <p className="text-[#E2D9F3] text-sm">{children}</p>
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div className="bg-[#8B5CF6]/20 border border-[#8B5CF6]/30 rounded-lg p-3 mt-3">
    <p className="text-sm text-[#E2D9F3]">
      <span className="text-[#CCFF00] font-semibold">💡 Tip:</span> {children}
    </p>
  </div>
);

const GuidePage = () => {
  return (
    <div className="min-h-screen bg-gradient-dark p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/">
            <Button variant="ghost" className="btn-ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="mx-auto mb-4">
            <Logo size="md" className="mx-auto" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
            How to Use GroupRotate
          </h1>
          <p className="text-xl text-[#E2D9F3]">
            Your complete guide to running workshops and quizzes
          </p>
        </motion.div>

        {/* Quick Start */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-heavy rounded-2xl p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Play className="w-6 h-6 text-[#00E676]" />
            Quick Start (2 minutes)
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 glass-light rounded-xl">
              <div className="text-3xl mb-2">1️⃣</div>
              <p className="text-white font-semibold">Create Session</p>
              <p className="text-sm text-[#E2D9F3]">Choose Quiz or Carousel mode</p>
            </div>
            <div className="text-center p-4 glass-light rounded-xl">
              <div className="text-3xl mb-2">2️⃣</div>
              <p className="text-white font-semibold">Share Code</p>
              <p className="text-sm text-[#E2D9F3]">Participants join via code or QR</p>
            </div>
            <div className="text-center p-4 glass-light rounded-xl">
              <div className="text-3xl mb-2">3️⃣</div>
              <p className="text-white font-semibold">Start & Facilitate</p>
              <p className="text-sm text-[#E2D9F3]">Control everything from your dashboard</p>
            </div>
          </div>
        </motion.div>

        {/* Sections */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {/* Getting Started */}
          <Section title="Getting Started" icon={Home} defaultOpen={true}>
            <Step number="1" title="Go to the Home Page">
              Visit the app and click <span className="text-[#E91E8C] font-semibold">"Create Session"</span> to start a new workshop or quiz.
            </Step>
            <Step number="2" title="Choose Your Mode">
              <span className="text-[#8B5CF6] font-semibold">Quiz Mode:</span> For team trivia, buzzer games, and scored competitions.
              <br />
              <span className="text-[#E91E8C] font-semibold">Carousel Mode:</span> For rotating workshop stations with discussion questions.
            </Step>
            <Step number="3" title="Configure Your Session">
              Enter a session name, number of teams/groups, and select an energizer mode (sound signals or movement signals for team formation).
            </Step>
            <Step number="4" title="Setup Phase">
              After creating, you're in <span className="text-[#FFC400] font-semibold">SETUP mode</span>. Here you can:
              <br />• Add quiz questions
              <br />• Configure stations
              <br />• Edit team names
              <br />• Participants <span className="text-[#FF1744]">cannot join yet</span>
            </Step>
            <Step number="5" title="Open Lobby">
              When ready, click <span className="text-[#8B5CF6] font-semibold">"Open Lobby"</span>. Now participants can join and see fun waiting messages!
            </Step>
            <Step number="6" title="Start Game">
              Once everyone is in, click <span className="text-[#00E676] font-semibold">"Start Game"</span> to go live!
            </Step>
            <Tip>
              Save your <span className="text-[#CCFF00]">Facilitator Code</span> - it lets you return to your session if you close the browser!
            </Tip>
          </Section>

          {/* For Participants */}
          <Section title="For Participants" icon={Users}>
            <Step number="1" title="Join the Session">
              Go to the app and click <span className="text-[#E91E8C] font-semibold">"Join Session"</span>, or scan the QR code shown by the facilitator.
            </Step>
            <Step number="2" title="Enter the Code">
              Type the 6-digit code shared by your facilitator (e.g., <span className="text-[#CCFF00]">ABC123</span>).
              <br /><span className="text-[#6B5E7C] italic">If the lobby isn't open yet, you'll see "Session not ready" - just wait!</span>
            </Step>
            <Step number="3" title="Enter Your Name">
              Type your name so your team knows who you are.
            </Step>
            <Step number="4" title="Wait in the Lobby">
              While waiting, you'll see fun motivational messages. You can also <span className="text-[#E91E8C]">edit your team name</span> by tapping on it!
            </Step>
            <Step number="5" title="Find Your Team">
              When the facilitator reveals the energizer, listen for your sound signal or watch for your movement - find others with the same signal! Your signal can appear right in the lobby, even before the game starts.
            </Step>
            <Step number="6" title="Answer Station Questions">
              In a workshop, each station may show instructions first (tap <span className="text-[#8B5CF6]">"Continue"</span>), then a step-by-step set of questions. Answer each one and tap Next - your progress bar fills as you go.
            </Step>
            <Step number="7" title="Become the Team Lead (optional)">
              One person per team can tap <span className="text-[#FFC400]">"I'm the Team Lead"</span> <Crown className="w-3.5 h-3.5 inline text-[#FFC400]" />. The lead answers <span className="text-white">team</span> questions on behalf of the group, while <span className="text-white">everyone</span> answers individual questions. Tap "Step down" to release the role.
            </Step>
            <Tip>
              Use the <span className="text-[#6B5E7C]">🔇</span> button in the top-right corner to mute sounds if needed.
            </Tip>
          </Section>

          {/* Quiz Mode */}
          <Section title="Quiz Mode - Running a Quiz" icon={Zap}>
            <h4 className="text-white font-semibold mb-3">As a Facilitator:</h4>
            
            <Step number="1" title="Add Questions">
              Click <span className="text-[#E91E8C]">"Manage Questions"</span> to add your quiz questions. You can include:
              <br />• Question text and correct answer (for your reference)
              <br />• Points value and time limit
              <br />• Images, YouTube videos, or audio files
            </Step>
            
            <Step number="2" title="Open Presentation View">
              Click <span className="text-[#8B5CF6]">"Open Projector View"</span> to open a full-screen view for your projector/TV.
            </Step>
            
            <Step number="3" title="Show Questions">
              Use the <span className="text-[#CCFF00]">Q1, Q2, Q3...</span> buttons to show each question on the projector.
            </Step>
            
            <Step number="4" title="Start Buzzer">
              Click <span className="text-[#FF1744]">"Start Buzzer"</span> and teams can buzz in to answer.
            </Step>
            
            <Step number="5" title="Score Points">
              After a team buzzes:
              <br />• Click <span className="text-[#00E676]">✓ Correct!</span> → Shows green animation, +100 points
              <br />• Click <span className="text-[#FF1744]">✗ Wrong!</span> → Shows red animation, optionally -50 points
              <br />• Click <span className="text-[#8B5CF6]">👁 Show Answer</span> → Reveals the correct answer
            </Step>
            
            <Step number="6" title="Final Results">
              When done, click <span className="text-[#FFD700]">"Final Reveal"</span> to show dramatic results from 4th to 1st place!
            </Step>
            
            <Tip>
              Use the quick point buttons (+100, -50, +50 steal) on buzz results for fast scoring!
            </Tip>
          </Section>

          {/* Presentation View */}
          <Section title="Presentation View Controls" icon={Monitor}>
            <p className="text-[#E2D9F3] mb-4">
              The Presentation View is designed to be shown on a projector or large screen. Control it from your Facilitator Dashboard.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="p-3 glass-light rounded-lg text-center">
                <span className="text-2xl">🏠</span>
                <p className="text-white text-sm font-semibold mt-1">Lobby</p>
                <p className="text-xs text-[#E2D9F3]">Shows QR code & join info</p>
              </div>
              <div className="p-3 glass-light rounded-lg text-center">
                <span className="text-2xl">❓</span>
                <p className="text-white text-sm font-semibold mt-1">Question</p>
                <p className="text-xs text-[#E2D9F3]">Shows current question</p>
              </div>
              <div className="p-3 glass-light rounded-lg text-center">
                <span className="text-2xl">⚡</span>
                <p className="text-white text-sm font-semibold mt-1">Buzzer</p>
                <p className="text-xs text-[#E2D9F3]">Shows buzz order</p>
              </div>
              <div className="p-3 glass-light rounded-lg text-center">
                <span className="text-2xl">🏆</span>
                <p className="text-white text-sm font-semibold mt-1">Final</p>
                <p className="text-xs text-[#E2D9F3]">Dramatic reveal</p>
              </div>
            </div>
            
            <Tip>
              The beautiful Aurora animation in the background uses your brand colors automatically!
            </Tip>
          </Section>

          {/* Carousel Mode */}
          <Section title="Carousel Mode - Workshop Rotations" icon={RotateCcw}>
            <h4 className="text-white font-semibold mb-3">Setting Up Stations:</h4>
            
            <Step number="1" title="Create Stations">
              When creating a Carousel session, specify the number of stations (e.g., 4 stations for a design thinking workshop).
            </Step>
            
            <Step number="2" title="Edit Station Content">
              Click a station card (pencil icon) to set its <span className="text-white">name</span>, optional <span className="text-white">instructions</span>, and a set of questions. Instructions appear on screen first and on participants' devices before the questions begin.
            </Step>

            <Step number="3" title="Choose Question Types & Scope">
              Each question can be one of: <span className="text-white">Short Text, Long Text, True/False, Ranking/Scale, Fill in the Blank</span>, or <span className="text-[#CCFF00]">Instruction / Info</span> (text only, no answer needed). You can also group questions into <span className="text-white">sub-sets (a, b, c)</span> and set each to <span className="text-[#8B5CF6]">Team</span> (only the Team Lead answers) or <span className="text-white">Individual</span> (everyone answers).
            </Step>

            <Step number="4" title="Show Only Instructions on the Projector (optional)">
              In the station editor, tick <span className="text-[#E91E8C]">"Show only the instructions on the projector station screen"</span> to hide the questions on the big screen. Participants still answer them on their own devices.
            </Step>

            <Step number="5" title="Open the Station Screens">
              Click <span className="text-[#8B5CF6]">"Station Screens"</span> to open the projector view. Each station shows its instructions and questions, with a countdown timer overlay.
            </Step>

            <Step number="6" title="Rotate Groups">
              Click <span className="text-[#E91E8C]">"Rotate All Groups"</span> to move every group to the next station simultaneously.
            </Step>

            <Step number="7" title="Control Timer">
              Set a timer for each rotation period. Participants will see a countdown and get alerts when time is up.
            </Step>

            <Tip>
              Groups are automatically balanced - if you have more groups than stations, multiple groups will share stations.
            </Tip>
          </Section>

          {/* Team Energizer */}
          <Section title="Team Formation Energizer" icon={Sparkles}>
            <p className="text-[#E2D9F3] mb-4">
              The energizer helps participants find their team in a fun, interactive way!
            </p>
            
            <h4 className="text-white font-semibold mb-2">Sound Signals:</h4>
            <p className="text-[#E2D9F3] mb-3 text-sm">
              Each team gets a unique animal sound. Participants make or listen for their sound to find teammates.
              <br />Examples: 🐱 Kitten Meow, 🦉 Owl Hoot, 🐺 Wolf Howl, 🐬 Dolphin Click
            </p>
            
            <h4 className="text-white font-semibold mb-2">Movement Signals:</h4>
            <p className="text-[#E2D9F3] mb-3 text-sm">
              Each team gets a unique movement. Great for loud environments!
              <br />Examples: 🦅 Flapping Wings, 🚣 Rowing Motion, 🗽 Statue Pose, 👋 Mexican Wave
            </p>
            
            <h4 className="text-white font-semibold mb-2">How to Use:</h4>
            <Step number="1" title="Open the Lobby">
              Once participants start joining, an <span className="text-[#E91E8C]">"Energizer Icebreaker"</span> card appears on your dashboard - you can run this <span className="text-white">before</span> starting the game.
            </Step>
            <Step number="2" title="Reveal Signals">
              Click <span className="text-[#8B5CF6]">"Reveal Signals"</span> - everyone instantly sees their team signal (on their phone and the Welcome screen). Click "Hide Signals" to take them down.
            </Step>
            <Step number="3" title="Find Your Team">
              Participants move around the room making/looking for their signal to find teammates.
            </Step>
            
            <Tip>
              Use "Mixed" mode for larger groups - combines sounds AND movements for more variety!
            </Tip>
          </Section>

          {/* Welcome Screen */}
          <Section title="Welcome Screen (Scan to Join)" icon={ScanLine}>
            <p className="text-[#E2D9F3] mb-4">
              A dedicated, always-on screen to project at the front of the room. It shows your QR code and join code at any time - perfect for getting people in and for latecomers.
            </p>
            <Step number="1" title="Open It">
              On your Facilitator Dashboard, click <span className="text-[#E91E8C]">"Welcome Screen"</span>. It opens in a new tab - drag it to your projector and press the fullscreen button.
            </Step>
            <Step number="2" title="What It Shows">
              Workshop name, a large <span className="text-white">QR code</span>, the <span className="text-[#CCFF00]">join code</span>, simple scan instructions, and live <span className="text-white">"X Teams / Y joined"</span> counts that update automatically.
            </Step>
            <Tip>
              Unlike the in-game projector view, the Welcome Screen never changes - keep it up the whole session so anyone can join whenever they arrive.
            </Tip>
          </Section>

          {/* Team Leads & Answers */}
          <Section title="Team Leads & Viewing Answers" icon={Crown}>
            <h4 className="text-white font-semibold mb-3">Assigning a Team Lead:</h4>
            <Step number="1" title="Participants Self-Select">
              Any participant can tap <span className="text-[#FFC400]">"I'm the Team Lead"</span> on their device. Only one lead per team - they answer the <span className="text-white">team-scoped</span> questions.
            </Step>
            <Step number="2" title="Or Assign It Yourself">
              In <span className="text-white">Teams Overview</span> on your dashboard, tap the <Crown className="w-3.5 h-3.5 inline text-[#FFC400]" /> crown next to any member to make them lead. Tap a different member to switch, or tap the current lead's crown to clear it.
            </Step>
            <h4 className="text-white font-semibold mb-3 mt-4">Viewing Answers:</h4>
            <Step number="3" title="Open the Answers View">
              Click <span className="text-[#8B5CF6]">"View Answers"</span> to see everything teams submitted, grouped by station and question, with filters by station and team. The Admin Panel has the same view across all sessions.
            </Step>
            <Tip>
              Team answers show once per team (from the lead); individual answers show one per person.
            </Tip>
          </Section>

          {/* Reset & Export */}
          <Section title="Reset Game & Offline Backup" icon={RotateCcw}>
            <h4 className="text-white font-semibold mb-3">Reset the Game (reuse a session):</h4>
            <Step number="1" title="Clear People & Results">
              Click <span className="text-[#FF9800]">"Reset Game"</span> (on the dashboard or Admin Panel) to remove <span className="text-white">all participants, answers, scores, buzzes, and team leads</span> and return the session to Setup.
            </Step>
            <Step number="2" title="Your Setup Is Kept">
              Stations, questions, and team names/avatars stay exactly as you built them - just re-open the lobby and run it again with a fresh group.
            </Step>
            <h4 className="text-white font-semibold mb-3 mt-4">Print a Backup (manual mode):</h4>
            <Step number="3" title="Export Details (PDF)">
              In the Stations section, click <span className="text-[#E91E8C]">"Export Details (PDF)"</span> <FileText className="w-3.5 h-3.5 inline text-[#E91E8C]" /> to download a printable run-sheet with every station's instructions and all questions (types, scopes, sub-questions). Great if you lose internet or want to run it manually.
            </Step>
            <Tip>
              Print the PDF before your session as a safety net - it includes questions even if you've hidden them from the projector screen.
            </Tip>
          </Section>

          {/* Tips & Tricks */}
          <Section title="Pro Tips & Tricks" icon={CheckCircle}>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Test before the session:</span> Create a test session and join as a participant on another device to see both views.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Use two screens:</span> Keep your Facilitator Dashboard on your laptop and the Presentation View on the projector.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Export results:</span> Click "Export CSV" to download all scores and session data for your records.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Project the Welcome Screen:</span> Keep it up at the front so people can scan and join at any point in your session.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Reuse a session:</span> Use "Reset Game" to clear people and results while keeping your stations, questions, and teams - then run it again with a new group.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Custom team names:</span> Click the pencil icon next to any team to give them a fun custom name!
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-black" />
                </div>
                <p className="text-[#E2D9F3] text-sm">
                  <span className="text-white font-semibold">Recover your session:</span> Lost your session? Go to the home page and click "I'm a Facilitator" to re-enter with your Facilitator Code.
                </p>
              </div>
            </div>
          </Section>

          {/* Troubleshooting */}
          <Section title="Troubleshooting" icon={HelpCircle}>
            <div className="space-y-4">
              <div className="p-3 glass-light rounded-lg">
                <p className="text-white font-semibold mb-1">Participant can't join?</p>
                <p className="text-[#E2D9F3] text-sm">Make sure they're using the correct join code and that you've clicked <span className="text-[#8B5CF6]">"Open Lobby"</span> - participants can join once the lobby is open (it doesn't need to be "Live" yet).</p>
              </div>
              
              <div className="p-3 glass-light rounded-lg">
                <p className="text-white font-semibold mb-1">Buzzer not working?</p>
                <p className="text-[#E2D9F3] text-sm">Ensure you clicked "Start Buzzer" first. Only one person per team can buzz - usually whoever is holding the team's designated device.</p>
              </div>
              
              <div className="p-3 glass-light rounded-lg">
                <p className="text-white font-semibold mb-1">Can't see updates?</p>
                <p className="text-[#E2D9F3] text-sm">Try refreshing the page. Updates happen in real-time, but occasionally a refresh helps.</p>
              </div>
              
              <div className="p-3 glass-light rounded-lg">
                <p className="text-white font-semibold mb-1">Lost your session?</p>
                <p className="text-[#E2D9F3] text-sm">Use your Facilitator Code on the home page to recover access. If you're a superadmin, you can also use the admin panel at <span className="text-[#CCFF00]">/admin</span>.</p>
              </div>
            </div>
          </Section>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12 pb-8"
        >
          <p className="text-[#6B5E7C] text-sm mb-4">
            Need more help? Contact your workshop facilitator.
          </p>
          <Link to="/">
            <Button className="btn-primary-gradient">
              <Home className="w-4 h-4 mr-2" />
              Go to Home Page
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default GuidePage;
